import { useState, useEffect } from 'react';
import type { Letterhead, LetterheadInput } from '../../types/index';

const INPUT_CLASS =
  'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

interface LetterheadFormProps {
  initial?: Letterhead | null;
  allowCityDefaultToggle?: boolean;  // shown only for city_admins
  defaultIsCityDefault?: boolean;    // initial value of the toggle
  onSubmit: (body: LetterheadInput) => Promise<unknown>;
  onCancel: () => void;
  isSubmitting?: boolean;
  submitLabel?: string;
}

function emptyInput(): LetterheadInput {
  return {
    name: '',
    sender_block: '',
    contact_line: '',
    signature_block: '',
    is_active: true,
    set_as_city_default: false,
  };
}

function fromLetterhead(lh: Letterhead): LetterheadInput {
  return {
    name: lh.name,
    sender_block: lh.sender_block,
    contact_line: lh.contact_line ?? '',
    signature_block: lh.signature_block ?? '',
    is_active: lh.is_active,
    set_as_city_default: lh.is_city_default,
  };
}

export default function LetterheadForm({
  initial = null,
  allowCityDefaultToggle = false,
  defaultIsCityDefault = false,
  onSubmit,
  onCancel,
  isSubmitting = false,
  submitLabel = 'Speichern',
}: LetterheadFormProps) {
  const [form, setForm] = useState<LetterheadInput>(() =>
    initial ? fromLetterhead(initial) : { ...emptyInput(), set_as_city_default: defaultIsCityDefault },
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (initial) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Reload the editable form when the selected letterhead changes.
      setForm(fromLetterhead(initial));
    }
  }, [initial]);

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.sender_block.trim()) {
      setError('Name und Absender-Block sind erforderlich.');
      return;
    }
    setError(null);
    try {
      await onSubmit(form);
    } catch {
      setError('Speichern fehlgeschlagen.');
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Name (intern)</label>
        <input
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className={INPUT_CLASS}
          placeholder="z. B. Stadt Bochum — Amt für Wohnen"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Absender (oben rechts auf dem Brief)
        </label>
        <textarea
          value={form.sender_block}
          onChange={(e) => setForm({ ...form, sender_block: e.target.value })}
          className={INPUT_CLASS}
          rows={4}
          placeholder={'Stadt Bochum\nAmt für Wohnen\nMusterstr. 1\n44787 Bochum'}
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Kontaktzeile (optional)
        </label>
        <input
          value={form.contact_line ?? ''}
          onChange={(e) => setForm({ ...form, contact_line: e.target.value })}
          className={INPUT_CLASS}
          placeholder="Tel: 0234-… | E-Mail: …"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Signatur (unter dem Brieftext)
        </label>
        <textarea
          value={form.signature_block ?? ''}
          onChange={(e) => setForm({ ...form, signature_block: e.target.value })}
          className={INPUT_CLASS}
          rows={3}
          placeholder={'Mit freundlichen Grüßen'}
        />
      </div>
      {allowCityDefaultToggle && (
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={!!form.set_as_city_default}
            onChange={(e) => setForm({ ...form, set_as_city_default: e.target.checked })}
          />
          Als City-Standard-Briefkopf festlegen (für alle Sachbearbeiter sichtbar)
        </label>
      )}
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex gap-2 pt-1">
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="btn btn-primary py-1.5"
        >
          {isSubmitting ? 'Wird gespeichert…' : submitLabel}
        </button>
        <button
          onClick={onCancel}
          className="btn btn-text text-neutral-600 enabled:hover:text-neutral-800 focus-visible:ring-neutral-500 text-sm"
        >
          Abbrechen
        </button>
      </div>
    </div>
  );
}
